/**
 * Безопасная работа с Clipboard API
 * Включает fallback на document.execCommand для старых браузеров
 */

/**
 * Копирует текст в буфер обмена
 * @param text - текст для копирования
 * @returns Promise<boolean> - успешность операции
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  // Современный Clipboard API
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fallback на execCommand если clipboard API не сработал
    }
  }

  // Fallback для старых браузеров и HTTP
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.cssText = 'position:fixed;left:-9999px;top:-9999px;opacity:0';
    textarea.setAttribute('readonly', '');
    document.body.appendChild(textarea);
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    const success = document.execCommand('copy');
    document.body.removeChild(textarea);
    return success;
  } catch {
    return false;
  }
};

/**
 * Копирует текст, который будет готов позже (например, после запроса к серверу).
 * Safari и Firefox разрешают запись в буфер только сразу по клику: если дождаться ответа
 * и потом вызвать writeText, запись запрещается. Поэтому запись начинаем СИНХРОННО в клике
 * через ClipboardItem с обещанием текста; где так нельзя — ждём текст и копируем обычно.
 * Вызывать прямо в обработчике клика, до любых await.
 * @param textPromise - обещание текста; отклонённое — копирование не выполняется
 * @returns Promise<boolean> - успешность операции (false и при отклонённом textPromise)
 */
export const copyToClipboardDeferred = async (textPromise: Promise<string>): Promise<boolean> => {
  const ClipboardItemCtor = (window as unknown as { ClipboardItem?: new (items: Record<string, Promise<Blob>>) => unknown }).ClipboardItem;
  if (ClipboardItemCtor && navigator.clipboard?.write) {
    const blobPromise = textPromise.then((text) => new Blob([ text ], { type: 'text/plain' }));
    try {
      // write вызывается синхронно — до первого await в этой функции
      await navigator.clipboard.write([ new ClipboardItemCtor({ 'text/plain': blobPromise }) as ClipboardItem ]);
      return true;
    } catch {
      // Браузер не принял обещание (старый Chrome) или текст не получен — пробуем обычный путь ниже
    }
  }
  try {
    const text = await textPromise;
    return await copyToClipboard(text);
  } catch {
    return false;
  }
};
